import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLocations } from "@/hooks/useLocations";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { DealCard } from "@/components/deals/DealCard";
import { Loader2, Plus } from "lucide-react";

function useDeals(params) {
  return useQuery({
    queryKey: ["deals", params],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/deals", { params: { country: "NZ", limit: 24, ...params } });
      return data.deals || data || [];
    },
    staleTime: 2 * 60_000,
  });
}

export default function DealsPage() {
  const [params, setParams] = useSearchParams();
  const { data: regions = [] } = useLocations();
  const { user } = useAuth();

  const q = { region: params.get("region") || "", city: params.get("city") || "", sort: params.get("sort") || "recent" };
  const deals = useDeals(q);

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Deals" description="Hot deals and special offers across New Zealand." />
      <Breadcrumbs items={[{ label: "Deals" }]} />
      <div className="mt-6 flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
        <div>
          <h1 className="font-display text-4xl font-black text-slate-900">Deals across Aotearoa</h1>
          <p className="mt-2 text-slate-500">Featured offers, limited-time discounts and free-quote services.</p>
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <Link
              to="/my/deals"
              data-testid="deals-page-my-deals-link"
              className="rounded-full border border-soft text-slate-700 hover:border-[color:var(--brand-blue)] font-semibold px-4 py-2.5 text-sm"
            >
              My deals
            </Link>
          )}
          <Link
            to="/deals/post"
            data-testid="deals-page-post-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 shadow-card"
          >
            <Plus className="w-4 h-4" /> Post your deal
          </Link>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <select value={q.region} onChange={(e) => set({ region: e.target.value, city: "" })} className="rounded-full bg-white border border-soft px-4 py-2 text-sm">
          <option value="">All of NZ</option>
          {regions.map((r) => <option key={r.region} value={r.region}>{r.region}</option>)}
        </select>
        <select value={q.sort} onChange={(e) => set({ sort: e.target.value })} className="rounded-full bg-white border border-soft px-4 py-2 text-sm">
          <option value="recent">Newest</option>
          <option value="price_asc">Price low → high</option>
          <option value="price_desc">Price high → low</option>
        </select>
      </div>

      {deals.isLoading && <div className="mt-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
      {!deals.isLoading && (deals.data || []).length === 0 && (
        <div className="mt-12 rounded-2xl border border-dashed border-soft bg-white p-14 text-center text-slate-500">
          No deals in this region yet.
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {(deals.data || []).map((d) => (
          <DealCard key={d.id} deal={d} />
        ))}
      </div>
    </div>
  );
}

